/**
 * Skeletons match the real row geometry exactly: `bg-raised` blocks at
 * `radius-xs`, 1.4s shimmer (components.md §3).
 *
 * **Never a centred spinner in a data panel.** Layout shift on arrival is the
 * thing skeletons exist to prevent, and on this product the arrival is a full
 * orderbook ladder landing inside a 47-second window — a panel that resizes when
 * it lands moves the ticket under the user's cursor.
 */

import { cx } from '@/lib/cx'

export interface SkeletonProps {
  width?: string | number
  height?: string | number
  radius?: 'xs' | 'sm' | 'full'
  className?: string
}

const RADIUS = { xs: 'rounded-xs', sm: 'rounded-sm', full: 'rounded-full' } as const

/** A number is design pixels, and goes through the scale unit like every other size. */
const length = (v: string | number | undefined) => (typeof v === 'number' ? `calc(${v} * var(--ud-u))` : v)

export function Skeleton({ width, height = 10, radius = 'xs', className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cx('ud-skeleton block', RADIUS[radius], className)}
      style={{ width: length(width), height: length(height) }}
    />
  )
}

/** A stack of skeleton rows at a given row height — the shape a table shows
 *  while its snapshot is in flight. */
export function SkeletonRows({
  rows = 6,
  rowHeight = 'var(--ud-row-height)',
  className,
}: {
  rows?: number
  rowHeight?: string
  className?: string
}) {
  return (
    <div role="status" aria-label="loading" className={cx('flex flex-col', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center border-b border-border-subtle px-2"
          style={{ height: rowHeight }}
        >
          <Skeleton width="40%" />
          <Skeleton width="20%" className="ml-auto" />
        </div>
      ))}
    </div>
  )
}
